import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Users, Plus, LoaderCircle, ShieldCheck, X, Info, Lock, Mail, Send, RotateCcw,
  Trash2, Search, ChevronUp, ChevronDown,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { useActiveOrg } from '@/lib/ActiveOrgContext';
import { usePermissions } from '@/lib/usePermissions';
import { formatInstant } from '@/lib/formatInstant';
import { useDisplayTimezone } from '@/lib/useDisplayTimezone';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { DestructiveActionDialog } from '@/components/shared/DestructiveActionDialog';
import { Pagination } from '@/components/shared/Pagination';
import type { OrgMember } from '@/types/api';
import {
  type Audience,
  deriveTabs,
  filterByTab,
  tabLabel,
  invitableRoles,
} from './users/audienceTabs';
import {
  type SortKey,
  type StatusFilter,
  ALL_STATUSES,
  applyPipeline,
  parseStateFromUrl,
  statusCounts,
  writeStateToParams,
} from './users/tableState';

const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
  active: 'Active',
  invited: 'Invited',
  disabled: 'Disabled',
};

/**
 * A2F-698 SSO banner copy helper. Grammatical join for 1, 2, or 3+ domains.
 *   1 → "X"
 *   2 → "X or Y"
 *   3+ → "X, Y, or Z"  (Oxford comma)
 */
function formatDomainList(domains: string[]): string {
  if (domains.length === 0) return '';
  if (domains.length === 1) return domains[0];
  if (domains.length === 2) return `${domains[0]} or ${domains[1]}`;
  return `${domains.slice(0, -1).join(', ')}, or ${domains[domains.length - 1]}`;
}

const ROLE_LABELS: Record<string, string> = {
  // A2F-698: `author` is the lowest-tier role (ribbon-only). Listed here so the
  // Settings → Users badge has a label. Muted color signals "lowest touch".
  author: 'Author',
  viewer: 'Viewer',
  contributor: 'Contributor',
  lead: 'Lead',
  admin: 'Admin',
};

const ROLE_COLORS: Record<string, string> = {
  author: 'bg-slate-100 text-slate-500',
  viewer: 'bg-slate-100 text-slate-600',
  contributor: 'bg-blue-100 text-blue-700',
  lead: 'bg-amber-100 text-amber-700',
  admin: 'bg-red-100 text-red-700',
};

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'text-emerald-600' },
  invited: { label: 'Invited', className: 'text-amber-600' },
  disabled: { label: 'Disabled', className: 'text-slate-400' },
};

export function ManageUsersPage() {
  const { user: currentUser } = useAuth();
  const { activeOrg } = useActiveOrg();
  const { canReadUsers, canManageUsers } = usePermissions();
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const orgId = currentUser?.orgId;
  const isPublicSignup = !!activeOrg?.isPublicSignup;

  const tabConfig = useMemo(
    () => deriveTabs(members, isPublicSignup),
    [members, isPublicSignup],
  );

  const activeTab: Audience = useMemo(() => {
    const fromUrl = searchParams.get('tab') as Audience | null;
    if (tabConfig && fromUrl && tabConfig.tabs.includes(fromUrl)) return fromUrl;
    return tabConfig?.defaultTab ?? 'all';
  }, [searchParams, tabConfig]);

  const handleTabChange = (next: Audience) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    // A2F-698: tab change resets pagination but preserves sort/filter/search
    // (commit 5 wires those URL params; this delete is a no-op until then,
    // and stays correct once they exist).
    params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const visibleMembers = useMemo(
    () => (tabConfig ? filterByTab(members, activeTab) : members),
    [members, activeTab, tabConfig],
  );

  const hasExistingAuthor = useMemo(
    () => members.some((m) => m.role === 'author'),
    [members],
  );

  const inviteRoleOptions = useMemo(
    () => invitableRoles(isPublicSignup, hasExistingAuthor),
    [isPublicSignup, hasExistingAuthor],
  );

  const loadMembers = async () => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const list = await api.orgs.members(orgId);
      setMembers(list);
    } catch {
      // auth might not be enabled or no permissions
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, [orgId]);

  const isAdmin = canManageUsers;

  // ── A2F-698 table pipeline (commit 5) ──
  // State is URL-derived (sort, status filter, search, page) so admins can
  // deep-link to a specific view. Self row is extracted from the pipeline
  // input and rendered separately so it stays at the top of every page;
  // pagination math still counts it in the total (per build review touch 3).
  const tableState = useMemo(
    () => parseStateFromUrl(searchParams),
    [searchParams],
  );

  // Search input has its own local state so typing doesn't update the URL on
  // every keystroke; a 250ms debounce flushes to URL.
  // Use the callback form of setSearchParams so the timeout reads the LATEST
  // URL params at flush time — protects against a stale-closure race where
  // the user types, then changes a tab/sort/filter inside the 250ms window;
  // capturing `searchParams` directly would clobber that concurrent write.
  const [searchInput, setSearchInput] = useState(tableState.search);
  useEffect(() => {
    if (searchInput === tableState.search) return;
    const id = window.setTimeout(() => {
      setSearchParams(
        (prev) => writeStateToParams(prev, { search: searchInput, page: 1 }),
        { replace: true },
      );
    }, 250);
    return () => window.clearTimeout(id);
  }, [searchInput, tableState.search, setSearchParams]);
  // Reset the local search when the URL drops it (e.g. user clears via
  // browser back button).
  useEffect(() => {
    if (tableState.search !== searchInput) setSearchInput(tableState.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.search]);

  const selfEmail = currentUser?.email?.toLowerCase();
  const selfRow = useMemo(
    () => visibleMembers.find((m) => m.email?.toLowerCase() === selfEmail) ?? null,
    [visibleMembers, selfEmail],
  );

  const pipelineCounts = useMemo(
    () => statusCounts(visibleMembers),
    [visibleMembers],
  );

  const pipelineResult = useMemo(
    () => applyPipeline(visibleMembers, tableState),
    [visibleMembers, tableState],
  );

  // Visible page rows minus the self row (self is rendered separately,
  // pinned to the top of every page; pagination total includes it).
  const visiblePageRows = useMemo(
    () =>
      selfRow
        ? pipelineResult.visible.filter(
            (m) => m.email?.toLowerCase() !== selfEmail,
          )
        : pipelineResult.visible,
    [pipelineResult.visible, selfRow, selfEmail],
  );

  const handleSortClick = (key: SortKey) => {
    const next = writeStateToParams(searchParams, {
      sortKey: key,
      sortOrder:
        tableState.sortKey === key
          ? tableState.sortOrder === 'asc' ? 'desc' : 'asc'
          : 'asc',
      page: 1,
    });
    setSearchParams(next, { replace: true });
  };

  const handleStatusToggle = (status: StatusFilter) => {
    const nextSet = new Set(tableState.statuses);
    if (nextSet.has(status)) nextSet.delete(status);
    else nextSet.add(status);
    setSearchParams(
      writeStateToParams(searchParams, { statuses: nextSet, page: 1 }),
      { replace: true },
    );
  };

  const handlePageChange = (next: number) => {
    setSearchParams(
      writeStateToParams(searchParams, { page: next }),
      { replace: true },
    );
  };

  // ── A2F-698 bulk actions (commit 6) ──
  // Selection keyed by orgUserId (stable across email changes). Self row
  // gets no checkbox so admins can't include themselves in a bulk Disable.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmBulkDisable, setConfirmBulkDisable] = useState(false);
  const [confirmBulkRevoke, setConfirmBulkRevoke] = useState(false);
  const [bulkResult, setBulkResult] = useState<
    | { kind: 'disable' | 'revoke'; successes: string[]; failures: { email: string; reason: string }[] }
    | null
  >(null);

  // Drop selections that no longer match any visible (post-pipeline) row —
  // e.g. status filter changed, search narrowed, page changed. Keeps the
  // bulk-action gate honest (we never bulk-act on rows the admin can no
  // longer see).
  useEffect(() => {
    const visibleIds = new Set(visiblePageRows.map((m) => m.orgUserId));
    setSelectedIds((prev) => {
      const next = new Set<string>();
      for (const id of prev) {
        if (visibleIds.has(id)) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [visiblePageRows]);

  const selectedRows = useMemo(
    () => visiblePageRows.filter((m) => selectedIds.has(m.orgUserId)),
    [visiblePageRows, selectedIds],
  );

  const allOnPageSelected =
    visiblePageRows.length > 0 && selectedIds.size === visiblePageRows.length;
  const someOnPageSelected = selectedIds.size > 0 && !allOnPageSelected;

  const allSelectedActive =
    selectedRows.length > 0 && selectedRows.every((m) => m.status === 'active');
  const allSelectedInvited =
    selectedRows.length > 0 && selectedRows.every((m) => m.status === 'invited');

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelectedIds((prev) => {
      if (prev.size === visiblePageRows.length) return new Set();
      return new Set(visiblePageRows.map((m) => m.orgUserId));
    });
  };

  const handleBulkDisable = async (rationale: string) => {
    if (!orgId) return;
    const emails = selectedRows.map((m) => m.email!).filter(Boolean);
    const res = await api.orgs.bulkDisable(orgId, emails, rationale);
    setConfirmBulkDisable(false);
    setSelectedIds(new Set());
    setBulkResult({ kind: 'disable', successes: res.successes, failures: res.failures });
    loadMembers();
  };

  const handleBulkRevoke = async (rationale: string) => {
    if (!orgId) return;
    const emails = selectedRows.map((m) => m.email!).filter(Boolean);
    const res = await api.orgs.bulkRevokeInvites(orgId, emails, rationale);
    setConfirmBulkRevoke(false);
    setSelectedIds(new Set());
    setBulkResult({ kind: 'revoke', successes: res.successes, failures: res.failures });
    loadMembers();
  };

  // Early returns AFTER all hooks (React rules-of-hooks). The pre-A2F-698
  // `if (loading) return ...` placement above the second batch of hooks
  // tripped React error #310 — fewer hooks ran on the loading render than
  // on subsequent renders. Hooks are now all unconditional.
  if (loading) return <LoadingSpinner message="Loading users..." />;

  if (!canReadUsers) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">
            You don't have permission to view user management.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
            21 CFR Part 11
          </p>
          <h1 className="text-xl font-bold text-brand-charcoal">User Management</h1>
          <p className="text-sm text-slate-500 mt-1">
            Invite users and manage role assignments.
          </p>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          disabled={!canManageUsers}
          title={!canManageUsers ? 'Requires Lead or Admin role' : undefined}
          className={`inline-flex items-center gap-2 px-4 py-2 bg-brand-orange text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors${!canManageUsers ? ' opacity-50 cursor-not-allowed' : ''}`}
        >
          <Plus className="w-4 h-4" />
          Invite User
        </button>
      </div>

      {/* Bulk-action result banner (A2F-698 commit 6).
          Dismissible. Appears after a Bulk Disable / Bulk Revoke completes
          and reports per-row outcomes so partial failures are visible
          without spelunking the audit log. */}
      {bulkResult && (
        <div
          className={`rounded-xl border p-4 flex items-start gap-3 ${
            bulkResult.failures.length === 0
              ? 'bg-emerald-50 border-emerald-200'
              : 'bg-amber-50 border-amber-200'
          }`}
        >
          <Info
            className={`w-5 h-5 mt-0.5 shrink-0 ${
              bulkResult.failures.length === 0 ? 'text-emerald-500' : 'text-amber-500'
            }`}
          />
          <div className="flex-1">
            <p className="text-sm font-medium text-brand-charcoal">
              {bulkResult.kind === 'disable' ? 'Bulk disable' : 'Bulk revoke'}:{' '}
              {bulkResult.successes.length} succeeded
              {bulkResult.failures.length > 0 &&
                `, ${bulkResult.failures.length} failed`}
            </p>
            {bulkResult.failures.length > 0 && (
              <ul className="mt-1 text-xs text-slate-600 space-y-0.5">
                {bulkResult.failures.map((f) => (
                  <li key={f.email}>
                    <span className="font-mono">{f.email}</span> — {f.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            onClick={() => setBulkResult(null)}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* SSO Info Banner (A2F-698 commit 7).
          Hidden entirely when the org has no configured SSO domains
          (CTDCommons, plain email-auth orgs) — the pre-A2F-698 banner was
          misleading there. When SSO is configured, names the configured
          domain(s) so admins know which users will be SSO-routed. */}
      {activeOrg && activeOrg.ssoDomains && activeOrg.ssoDomains.length > 0 && (
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-blue-800 font-medium">Authentication</p>
            <p className="text-sm text-blue-700 mt-0.5">
              Users with an email at{' '}
              <span className="font-medium">
                {formatDomainList(activeOrg.ssoDomains)}
              </span>{' '}
              sign in with Microsoft (Entra ID). External collaborators
              receive a magic link via email.
            </p>
          </div>
        </div>
      )}

      {/* Audience tabs (A2F-698). Render only when at least one author user
          exists in the org (CTDCommons today, future mixed-audience customers
          tomorrow). Single-audience orgs see no tabs — identical UX to the
          pre-A2F-698 page. */}
      {tabConfig && (
        <div className="border-b border-slate-200">
          <nav className="flex gap-1" aria-label="Audience tabs">
            {tabConfig.tabs.map((tab) => {
              const isActive = tab === activeTab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => handleTabChange(tab)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    isActive
                      ? 'border-brand-orange text-brand-charcoal'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-200'
                  }`}
                >
                  {tabLabel(tab)}
                </button>
              );
            })}
          </nav>
        </div>
      )}

      {/* Filter pills + search (A2F-698 commit 5).
          Counts come from the tab-filtered universe BEFORE the status
          filter is applied, so the chip number doesn't drop to zero
          when the admin toggles a status off. */}
      {visibleMembers.length > 0 && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            {ALL_STATUSES.map((status) => {
              const active = tableState.statuses.has(status);
              const count = pipelineCounts[status];
              return (
                <button
                  key={status}
                  type="button"
                  onClick={() => handleStatusToggle(status)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? 'border-brand-orange bg-brand-orange/10 text-brand-charcoal'
                      : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                  }`}
                >
                  {STATUS_FILTER_LABELS[status]}
                  <span className="text-slate-400 font-normal">({count})</span>
                </button>
              );
            })}
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name or email"
              className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange w-64"
            />
          </div>
        </div>
      )}

      {/* Bulk action bar (A2F-698 commit 6). Visible when ≥1 row checked.
          Buttons gate on selection homogeneity: Bulk Disable only if every
          selected row is Active; Bulk Revoke only if every selected row is
          Invited. Tooltip on the disabled state explains why. */}
      {isAdmin && selectedIds.size > 0 && (
        <div className="bg-brand-charcoal text-white rounded-xl px-4 py-2.5 flex items-center justify-between gap-3">
          <p className="text-sm">
            <span className="font-semibold">{selectedIds.size}</span> selected
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-slate-300 hover:text-white px-2 py-1"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setConfirmBulkDisable(true)}
              disabled={!allSelectedActive}
              title={
                !allSelectedActive
                  ? 'Selection must contain only Active users'
                  : undefined
              }
              className="text-xs font-medium px-3 py-1 rounded bg-red-600 hover:bg-red-500 disabled:bg-slate-600 disabled:cursor-not-allowed"
            >
              Bulk disable
            </button>
            <button
              type="button"
              onClick={() => setConfirmBulkRevoke(true)}
              disabled={!allSelectedInvited}
              title={
                !allSelectedInvited
                  ? 'Selection must contain only Invited users'
                  : undefined
              }
              className="text-xs font-medium px-3 py-1 rounded bg-red-600 hover:bg-red-500 disabled:bg-slate-600 disabled:cursor-not-allowed"
            >
              Bulk revoke
            </button>
          </div>
        </div>
      )}

      {pipelineResult.totalAfterFilter === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">
            {visibleMembers.length === 0
              ? tabConfig
                ? `No ${tabLabel(activeTab).toLowerCase()} users in this organization.`
                : 'No users in this organization. Invite someone to get started.'
              : 'No users match the current filters.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                {isAdmin && (
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someOnPageSelected;
                      }}
                      onChange={toggleAllOnPage}
                      disabled={visiblePageRows.length === 0}
                      aria-label="Select all on this page"
                      className="rounded border-slate-300 text-brand-orange focus:ring-brand-orange"
                    />
                  </th>
                )}
                <SortHeader
                  label="User"
                  sortKey="user"
                  active={tableState.sortKey}
                  order={tableState.sortOrder}
                  onSort={handleSortClick}
                />
                <SortHeader
                  label="Role"
                  sortKey="role"
                  active={tableState.sortKey}
                  order={tableState.sortOrder}
                  onSort={handleSortClick}
                />
                <th className="text-left px-6 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  Auth
                </th>
                <SortHeader
                  label="Status"
                  sortKey="status"
                  active={tableState.sortKey}
                  order={tableState.sortOrder}
                  onSort={handleSortClick}
                />
                <SortHeader
                  label="Last Login"
                  sortKey="lastLogin"
                  active={tableState.sortKey}
                  order={tableState.sortOrder}
                  onSort={handleSortClick}
                />
                {isAdmin && (
                  <th className="text-right px-6 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {/* Self row pinned at the top of every page (A2F-698 review touch 3).
                  Excluded from the visible page rows below; included in the
                  pagination total. Self row gets no checkbox — admins can't
                  bulk-disable themselves. */}
              {selfRow && (
                <>
                  <MemberRow
                    key={`self-${selfRow.orgUserId}`}
                    member={selfRow}
                    orgId={orgId!}
                    isSelf
                    isAdmin={isAdmin}
                    onUpdate={loadMembers}
                    selected={false}
                    onToggleSelect={null}
                  />
                  <tr aria-hidden="true">
                    <td
                      colSpan={isAdmin ? 7 : 5}
                      className="border-t-2 border-slate-200 p-0"
                    />
                  </tr>
                </>
              )}
              {visiblePageRows.map((m) => (
                <MemberRow
                  key={m.orgUserId}
                  member={m}
                  orgId={orgId!}
                  isSelf={false}
                  isAdmin={isAdmin}
                  onUpdate={loadMembers}
                  selected={selectedIds.has(m.orgUserId)}
                  onToggleSelect={() => toggleRow(m.orgUserId)}
                />
              ))}
            </tbody>
          </table>
          {pipelineResult.pageCount > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-slate-200 bg-slate-50/30">
              <p className="text-xs text-slate-500">
                Showing{' '}
                <span className="font-medium text-slate-700">
                  {visiblePageRows.length + (selfRow ? 1 : 0)}
                </span>{' '}
                of{' '}
                <span className="font-medium text-slate-700">
                  {pipelineResult.totalAfterFilter}
                </span>{' '}
                users
              </p>
              <Pagination
                page={pipelineResult.page}
                pageCount={pipelineResult.pageCount}
                onPageChange={handlePageChange}
                itemLabel="users"
              />
            </div>
          )}
        </div>
      )}

      {/* Bulk-action confirmation dialogs. Standalone (not nested in the
          table tr/td), so no row-wrapper trick is needed here. */}
      <DestructiveActionDialog
        open={confirmBulkDisable}
        title={`Disable ${selectedRows.length} user${selectedRows.length === 1 ? '' : 's'}`}
        description={
          <>
            Disable {selectedRows.length} user{selectedRows.length === 1 ? '' : 's'}?
            {' '}They will lose access to Chrona immediately. The same rationale
            will be recorded on every audit row.
          </>
        }
        confirmLabel="Disable users"
        rationalePlaceholder="Why are you disabling these users?"
        onConfirm={handleBulkDisable}
        onCancel={() => setConfirmBulkDisable(false)}
      />
      <DestructiveActionDialog
        open={confirmBulkRevoke}
        title={`Revoke ${selectedRows.length} invitation${selectedRows.length === 1 ? '' : 's'}`}
        description={
          <>
            Revoke {selectedRows.length} pending invitation
            {selectedRows.length === 1 ? '' : 's'}? They will no longer be
            able to accept. The same rationale will be recorded on every
            audit row.
          </>
        }
        confirmLabel="Revoke invitations"
        rationalePlaceholder="Why are you revoking these invitations?"
        onConfirm={handleBulkRevoke}
        onCancel={() => setConfirmBulkRevoke(false)}
      />

      {showInvite && orgId && (
        <InviteDialog
          orgId={orgId}
          availableRoles={inviteRoleOptions}
          onClose={() => setShowInvite(false)}
          onSuccess={() => { setShowInvite(false); loadMembers(); }}
        />
      )}
    </div>
  );
}

function SortHeader({
  label,
  sortKey,
  active,
  order,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  order: 'asc' | 'desc';
  onSort: (key: SortKey) => void;
}) {
  const isActive = active === sortKey;
  return (
    <th className="text-left px-6 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-slate-700 ${
          isActive ? 'text-slate-700' : ''
        }`}
        aria-sort={
          isActive ? (order === 'asc' ? 'ascending' : 'descending') : 'none'
        }
      >
        {label}
        {isActive && (
          order === 'asc'
            ? <ChevronUp className="w-3 h-3" aria-hidden="true" />
            : <ChevronDown className="w-3 h-3" aria-hidden="true" />
        )}
      </button>
    </th>
  );
}


function MemberRow({
  member,
  orgId,
  isSelf,
  isAdmin,
  onUpdate,
  selected,
  onToggleSelect,
}: {
  member: OrgMember;
  orgId: string;
  isSelf: boolean;
  isAdmin: boolean;
  onUpdate: () => void;
  /** A2F-698: row selection state for bulk actions. */
  selected: boolean;
  /** A2F-698: callback to toggle this row's selection. `null` for the self
   *  row (no checkbox rendered). */
  onToggleSelect: (() => void) | null;
}) {
  const tz = useDisplayTimezone();
  const [editRole, setEditRole] = useState(false);
  const [role, setRole] = useState(member.role);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // A2F-699: per-action confirmation modals replace the legacy one-click flows.
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [confirmEnable, setConfirmEnable] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  // A2F-698: 60s resend cooldown UX. Local to the row so a second click in
  // the cooldown window can't fire even before the backend rate limit
  // (1/min per email) replies. Forces re-render at the 60s mark to flip the
  // button back to enabled.
  const [lastResentAt, setLastResentAt] = useState<number | null>(null);
  const [, forceRerender] = useState(0);
  useEffect(() => {
    if (!lastResentAt) return;
    const remaining = Math.max(0, 60_000 - (Date.now() - lastResentAt));
    if (remaining === 0) return;
    const id = window.setTimeout(() => forceRerender((n) => n + 1), remaining);
    return () => window.clearTimeout(id);
  }, [lastResentAt]);

  const cooldownMs = lastResentAt
    ? Math.max(0, 60_000 - (Date.now() - lastResentAt))
    : 0;
  const resendDisabled = cooldownMs > 0;
  const resendLabel = (() => {
    if (!lastResentAt) return 'Resend';
    if (cooldownMs > 0) return 'Sent just now';
    const minutes = Math.floor((Date.now() - lastResentAt) / 60_000);
    return `Sent ${minutes} min ago`;
  })();

  const handleRoleChange = async (newRole: string) => {
    if (!member.email) return;
    setSaving(true);
    setError('');
    try {
      await api.orgs.changeRole(orgId, member.email, newRole);
      setRole(newRole);
      setEditRole(false);
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDisable = async (rationale: string) => {
    if (!member.email) return;
    setError('');
    await api.orgs.disable(orgId, member.email, rationale);
    setConfirmDisable(false);
    onUpdate();
  };

  const handleConfirmEnable = async (rationale: string) => {
    if (!member.email) return;
    setError('');
    await api.orgs.enable(orgId, member.email, rationale);
    setConfirmEnable(false);
    onUpdate();
  };

  const handleResendInvite = async () => {
    if (!member.email || resendDisabled) return;
    setError('');
    try {
      await api.orgs.resendInvite(orgId, member.email);
      setLastResentAt(Date.now());
      onUpdate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to resend invite';
      // A2F-698: backend rate-limits resends (1/min per email, 5/hr per
      // inviter). Render a friendly message rather than the raw 429.
      if (msg.includes('429') || msg.toLowerCase().includes('rate')) {
        setError('This invite was resent recently. Try again shortly.');
        setLastResentAt(Date.now());
      } else {
        setError(msg);
      }
    }
  };

  const handleConfirmRevoke = async (rationale: string) => {
    if (!member.email) return;
    setError('');
    try {
      await api.orgs.revokeInvite(orgId, member.email, rationale);
      setConfirmRevoke(false);
      onUpdate();
    } catch (err) {
      // 409 means the invitation was accepted between page load and click —
      // refresh the list so the row's status flips to Active and the admin
      // can use the Disable flow instead. Re-throw any other error so
      // DestructiveActionDialog can surface it inline.
      const message = err instanceof Error ? err.message : 'Failed to revoke invitation';
      if (message.includes('409')) {
        setError('This user just accepted the invitation. Refreshing…');
        setConfirmRevoke(false);
        onUpdate();
        return;
      }
      throw err;
    }
  };

  const statusInfo = STATUS_STYLES[member.status] || STATUS_STYLES.active;

  return (
    <>
      <tr className={member.status === 'disabled' ? 'opacity-50' : ''}>
        {isAdmin && (
          <td className="px-4 py-3 w-10">
            {onToggleSelect ? (
              <input
                type="checkbox"
                checked={selected}
                onChange={onToggleSelect}
                aria-label={`Select ${member.email ?? member.orgUserId}`}
                className="rounded border-slate-300 text-brand-orange focus:ring-brand-orange"
              />
            ) : null}
          </td>
        )}
        <td className="px-6 py-3">
          <div>
            <p className="font-medium text-brand-charcoal">
              {member.displayName || '\u2014'}
              {isSelf && <span className="ml-1.5 text-xs text-slate-400">(you)</span>}
            </p>
            <p className="text-xs text-slate-400">{member.email}</p>
          </div>
        </td>
        <td className="px-6 py-3">
          {editRole ? (
            <select
              value={role}
              onChange={(e) => handleRoleChange(e.target.value)}
              disabled={saving}
              className="text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-orange"
            >
              {Object.entries(ROLE_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          ) : (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${ROLE_COLORS[member.role] || 'bg-slate-100 text-slate-600'}`}
              onClick={() => isAdmin && !isSelf && setEditRole(true)}
              style={{ cursor: isAdmin && !isSelf ? 'pointer' : 'default' }}
            >
              {member.role === 'admin' && <ShieldCheck className="w-3 h-3" />}
              {ROLE_LABELS[member.role] || member.role}
            </span>
          )}
        </td>
        <td className="px-6 py-3">
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            {member.authMethod === 'sso' ? (
              <>
                <Lock className="w-3 h-3" />
                SSO
              </>
            ) : (
              <>
                <Mail className="w-3 h-3" />
                Email
              </>
            )}
          </span>
        </td>
        <td className="px-6 py-3">
          <span className={`text-xs font-medium ${statusInfo.className}`}>
            {statusInfo.label}
          </span>
        </td>
        <td className="px-6 py-3 text-xs text-slate-500">
          {member.lastLoginAt ? formatInstant(member.lastLoginAt, tz) : 'Never'}
        </td>
        {isAdmin && (
          <td className="px-6 py-3 text-right space-x-2">
            {!isSelf && (
              <>
                {member.status === 'invited' && (
                  <>
                    <button
                      onClick={handleResendInvite}
                      disabled={resendDisabled}
                      className="text-xs text-brand-orange hover:text-orange-700 inline-flex items-center gap-1 disabled:text-slate-400 disabled:hover:text-slate-400 disabled:cursor-not-allowed"
                      title={
                        resendDisabled
                          ? 'Wait 60 seconds before resending'
                          : 'Resend invite'
                      }
                    >
                      <RotateCcw className="w-3 h-3" />
                      {resendLabel}
                    </button>
                    <button
                      onClick={() => setConfirmRevoke(true)}
                      className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1"
                      title="Revoke invitation"
                    >
                      <Trash2 className="w-3 h-3" />
                      Revoke
                    </button>
                  </>
                )}
                {member.status === 'active' && (
                  <button
                    onClick={() => setConfirmDisable(true)}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Disable
                  </button>
                )}
                {member.status === 'disabled' && (
                  <button
                    onClick={() => setConfirmEnable(true)}
                    className="text-xs text-emerald-600 hover:text-emerald-700"
                  >
                    Enable
                  </button>
                )}
              </>
            )}
          </td>
        )}
      </tr>
      {error && (
        <tr>
          <td colSpan={isAdmin ? 7 : 5} className="px-6 py-2">
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-1.5">
              {error}
            </div>
          </td>
        </tr>
      )}
      {/* Dialog row exists only while one of the three actions is open.  */}
      {/* Wrapping in tr/td keeps the overlay div from being a direct     */}
      {/* child of <tbody> (invalid HTML). p-0 + the dialog's             */}
      {/* position:fixed means the row contributes no visible height.    */}
      {/* (Earlier attempt used `tr.hidden` for the same goal — that is  */}
      {/* `display:none`, which propagates to descendants and hides the  */}
      {/* dialog itself. position:fixed does NOT escape display:none.)   */}
      {(confirmDisable || confirmEnable || confirmRevoke) && (
        <tr>
          <td colSpan={isAdmin ? 7 : 5} className="p-0">
            <DestructiveActionDialog
              open={confirmDisable}
              title="Disable user"
              description={
                <>
                  Disable <span className="font-medium">{member.email}</span>? They
                  will lose access to Chrona immediately.
                </>
              }
              confirmLabel="Disable user"
              rationalePlaceholder="Why are you disabling this user?"
              onConfirm={handleConfirmDisable}
              onCancel={() => setConfirmDisable(false)}
            />
            <DestructiveActionDialog
              open={confirmEnable}
              title="Re-enable user"
              description={
                <>
                  Re-enable <span className="font-medium">{member.email}</span>?
                  They will regain access to Chrona on their next sign-in.
                </>
              }
              confirmLabel="Re-enable user"
              confirmVariant="warning"
              rationalePlaceholder="Why are you re-enabling this user?"
              onConfirm={handleConfirmEnable}
              onCancel={() => setConfirmEnable(false)}
            />
            <DestructiveActionDialog
              open={confirmRevoke}
              title="Revoke invitation"
              description={
                <>
                  Revoke invitation for <span className="font-medium">{member.email}</span>?
                  They will no longer be able to accept it.
                </>
              }
              confirmLabel="Revoke invitation"
              rationalePlaceholder="Why are you revoking this invitation?"
              onConfirm={handleConfirmRevoke}
              onCancel={() => setConfirmRevoke(false)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function InviteDialog({
  orgId,
  availableRoles,
  onClose,
  onSuccess,
}: {
  orgId: string;
  availableRoles: string[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState(
    availableRoles.includes('viewer') ? 'viewer' : availableRoles[0],
  );
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ email: string; authMethod: string } | null>(null);

  const handleInvite = async () => {
    setError('');
    if (!firstName.trim()) {
      setError('First name is required');
      return;
    }
    if (!lastName.trim()) {
      setError('Last name is required');
      return;
    }
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.orgs.invite(orgId, email.trim(), firstName.trim(), lastName.trim(), role);
      setResult({ email: res.email, authMethod: res.authMethod });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-brand-charcoal">Invite User</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {result ? (
          <div className="px-6 py-6 text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-green-50 rounded-full flex items-center justify-center">
              <Send className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-sm font-medium text-brand-charcoal mb-1">
              Invited {result.email}
            </p>
            <p className="text-xs text-slate-500">
              {result.authMethod === 'email'
                ? 'A magic link has been sent to their email.'
                : "They'll sign in with Microsoft SSO."}
            </p>
            <button
              onClick={onSuccess}
              className="mt-4 px-4 py-2 text-sm font-medium text-white bg-brand-orange rounded-lg hover:bg-orange-600"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-brand-charcoal mb-1">First Name</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Jane"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-charcoal mb-1">Last Name</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Smith"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@company.com"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-1">Role</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
                >
                  {availableRoles.map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                  ))}
                </select>
              </div>
              {error && (
                <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                disabled={submitting}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-orange rounded-lg hover:bg-orange-600 disabled:opacity-50"
              >
                {submitting && <LoaderCircle className="w-4 h-4 animate-spin" />}
                <Send className="w-4 h-4" />
                Send Invite
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
